import { apiGet, apiPost, apiDelete } from "@/lib/api/client";
import type { PaginatedParams, PaginatedResponse } from "@/lib/api/types";

export const moderationApi = {
  listReports: (params?: PaginatedParams & { status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.status) searchParams.set("status", params.status);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/reports?${searchParams.toString()}`);
  },

  getReport: (id: string) => apiGet<{ data: Record<string, unknown> }>(`/moderation/reports/${id}`),

  takeAction: (id: string, body: Record<string, unknown>) => apiPost<{ data: Record<string, unknown> }>(`/moderation/reports/${id}/action`, body),

  dismissReport: (id: string) => apiPost<{ data: Record<string, unknown> }>(`/moderation/reports/${id}/dismiss`),

  warnUser: (userId: string, reason: string) => apiPost<{ data: Record<string, unknown> }>(`/moderation/users/${userId}/warn`, { reason }),

  suspendUser: (userId: string, body: Record<string, unknown>) => apiPost<{ data: Record<string, unknown> }>(`/moderation/users/${userId}/suspend`, body),

  restoreUser: (userId: string) => apiPost<{ data: Record<string, unknown> }>(`/moderation/users/${userId}/restore`),

  getAuditLogs: (params?: PaginatedParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/audit-logs?${searchParams.toString()}`);
  },

  getDashboard: () => apiGet<{ data: Record<string, unknown> }>("/moderation/dashboard"),

  listFlaggedContent: (params?: PaginatedParams & { type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.type) searchParams.set("type", params.type);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/content?${searchParams.toString()}`);
  },

  listSpamContent: (params?: PaginatedParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/spam?${searchParams.toString()}`);
  },

  listDiscussionsNeedingReview: (params?: PaginatedParams & { status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.status) searchParams.set("status", params.status);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/discussions?${searchParams.toString()}`);
  },

  listCommentsNeedingReview: (params?: PaginatedParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/comments?${searchParams.toString()}`);
  },

  listAppeals: (params?: PaginatedParams & { status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.status) searchParams.set("status", params.status);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/appeals?${searchParams.toString()}`);
  },

  resolveAppeal: (id: string, decision: string, note?: string) => apiPost<{ data: Record<string, unknown> }>(`/moderation/appeals/${id}/resolve`, { decision, note }),

  getModerationLogs: (params?: PaginatedParams & { targetType?: string; actionType?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.targetType) searchParams.set("targetType", params.targetType);
    if (params?.actionType) searchParams.set("actionType", params.actionType);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`/moderation/logs?${searchParams.toString()}`);
  },

  takeModerationAction: (body: Record<string, unknown>) => apiPost<{ data: Record<string, unknown> }>("/moderation/action", body),

  restoreContent: (body: Record<string, unknown>) => apiPost<{ data: Record<string, unknown> }>("/moderation/restore", body),

  bulkDismissReports: (reportIds: string[]) => apiPost<{ data: number }>("/moderation/reports/bulk-dismiss", { reportIds }),

  bulkPurgeContent: (contentItems: Record<string, unknown>[]) => apiPost<{ data: number }>("/moderation/content/bulk-purge", { contentItems }),

  getStats: () => apiGet<{ data: Record<string, unknown> }>("/moderation/stats"),

  export: (type = "reports", format = "CSV") => apiGet<{ data: unknown }>(`/moderation/export?type=${type}&format=${format}`),
};
